/**
 * Algorithm 3: Score Decay (Forgetting Curve)
 * Reduce section scores over time to model forgetting
 * 
 * @param {Object} sectionData - Section's current score and metadata
 * @param {Array} allSections - All sections (to calculate position)
 * @returns {Object} - Updated section data with decay applied
 */

import { DECAY } from '@config/constants.js';

export function applyScoreDecay(sectionData, allSections) {
  const { score, lastStudied, sectionId } = sectionData;

  const currentTime = Date.now();
  const hoursSinceStudy = (currentTime - lastStudied) / (1000 * 60 * 60);

  // Base decay: 2% per day = 0.0833% per hour
  let decayRate = DECAY.BASE_RATE_PER_DAY / 24;

  // MODIFIER 1: Recent sections decay slower
  const sectionIndex = allSections.findIndex(s => s.sectionId === sectionId);
  const totalSections = allSections.length;
  const recencyFactor = (totalSections - sectionIndex) / totalSections;
  // Most recent section (index 4 of 5): factor = 0.2 → decay at 60% rate
  // Oldest section (index 0 of 5): factor = 1.0 → decay at 100% rate
  decayRate *= 1 - recencyFactor * DECAY.RECENCY_PROTECTION;

  // MODIFIER 2: High mastery sections decay slower
  if (score > 70) {
    const masteryFactor = (score - 70) / 30; // 0 to 1
    // 70% score: factor = 0 → no reduction
    // 100% score: factor = 1 → decay at 40% rate
    decayRate *= 1 - masteryFactor * DECAY.MASTERY_PROTECTION;
  }

  // Calculate decay amount
  const decayAmount = score * decayRate * hoursSinceStudy;

  // Apply decay (minimum 0%)
  const newScore = Math.max(0, score - decayAmount);

  return {
    ...sectionData,
    score: newScore,
    decayedAmount: score - newScore, // Track how much was lost
  };
}
